'use client'

import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import styles from './styles/tech-slider.module.css'

export const RunSafeInTheDarkTechSlider = () => {
  const t = useTranslations()

  const problems = [
    {
      image: '/image/2026/09/18/Frame_880722.jpg',
      title: t('safe_in_the_dark_hardcode.tech_slider.slide_1.problem_1_title'),
      description: t(
        'safe_in_the_dark_hardcode.tech_slider.slide_1.problem_1_description'
      ),
      overlay: 'bg-neutral-900/40',
    },
    {
      image: '/image/2026/09/18/Frame_88073112.jpg',
      title: t('safe_in_the_dark_hardcode.tech_slider.slide_1.problem_2_title'),
      description: t(
        'safe_in_the_dark_hardcode.tech_slider.slide_1.problem_2_description'
      ),
      overlay: 'bg-primary/40 mix-blend-color',
    },
  ]

  const movementItems = [
    {
      image: '/image/2026/09/18/image.jpg',
      description: t('safe_in_the_dark_hardcode.tech_slider.slide_3.item_1'),
    },
    {
      image: '/image/2026/09/18/image-1.jpg',
      description: t('safe_in_the_dark_hardcode.tech_slider.slide_3.item_2'),
    },
    {
      image: '/image/2026/09/18/image-2.jpg',
      description: t('safe_in_the_dark_hardcode.tech_slider.slide_3.item_3'),
    },
  ]

  return (
    <Carousel
      opts={{ loop: true }}
      plugins={[Autoplay({ delay: 5000, stopOnInteraction: false })]}
      className='w-full bg-neutral-900'
    >
      <CarouselContent className='ml-0 items-center'>
        {/* Slide 1 — In the dark, being seen matters */}
        <CarouselItem className='flex flex-col items-center justify-center gap-6 bg-neutral-900 px-5 py-10 pb-16 pl-0 md:gap-8 lg:px-15 lg:py-14 lg:pb-20'>
          <div className='relative grid w-full grid-cols-1 overflow-hidden rounded-2xl sm:grid-cols-2'>
            {problems.map((problem, index) => (
              <div key={index} className='relative aspect-[900/524] w-full'>
                <Image
                  src={ImageHelper.getImageUrlCdn(problem.image)}
                  alt={problem.title}
                  fill
                  className='object-cover'
                />
                <div className={cn('absolute inset-0', problem.overlay)} />
                <div className='absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 p-4 text-center text-white md:p-6'>
                  <h3
                    className={cn(
                      styles.textGlowStrong,
                      'font-criteria text-base font-semibold uppercase leading-tight md:text-2xl 2xl:text-[28px]'
                    )}
                  >
                    {problem.title}
                  </h3>
                  <p className='hidden text-base text-neutral-200 sm:block md:text-lg'>
                    {problem.description}
                  </p>
                </div>
              </div>
            ))}

            <h2 className='pointer-events-none absolute inset-x-0 top-4 text-center font-criteria text-xl uppercase leading-tight text-white md:top-8 md:text-3xl 2xl:text-[55px] 2xl:leading-14'>
              <span className='font-extralight'>
                {t(
                  'safe_in_the_dark_hardcode.tech_slider.slide_1.title_line_1'
                )}
              </span>
              <br />
              <span className={cn(styles.textGlow, 'font-medium')}>
                {t(
                  'safe_in_the_dark_hardcode.tech_slider.slide_1.title_line_2'
                )}
              </span>
            </h2>
          </div>

          <div className='flex flex-col items-center gap-2 text-center'>
            <div className='h-1 w-[100px] rounded-full bg-gradient-to-r from-neutral-700 to-[#A291FF]' />
            <h2 className='bg-gradient-to-r from-[#a291ff] to-white bg-clip-text font-criteria text-xl font-semibold text-transparent md:text-4xl 2xl:text-[44px]'>
              {t('safe_in_the_dark_hardcode.tech_slider.slide_1.tagline')}
            </h2>
            <p className='text-sm text-neutral-300 md:text-base 2xl:text-lg'>
              {t(
                'safe_in_the_dark_hardcode.tech_slider.slide_1.tagline_description'
              )}
            </p>
          </div>
        </CarouselItem>

        {/* Slide 2 — Công nghệ 360Reflect */}
        <CarouselItem className='relative grid grid-cols-1 items-center justify-center gap-8 overflow-hidden bg-neutral-900 px-5 py-10 pb-16 pl-0 lg:grid-cols-[42fr_58fr] lg:px-15 lg:py-14 lg:pb-20'>
          <div className={cn(styles.glowBlob, styles.glowBlobTopLeft)} />
          <div className={cn(styles.glowBlob, styles.glowBlobBottomCenter)} />

          <div className='relative w-full'>
            <h2
              className={cn(
                styles.textGlow,
                'font-criteria text-[28px] font-semibold uppercase italic leading-tight text-white md:text-6xl md:leading-tight 2xl:text-7xl'
              )}
            >
              {t.rich('safe_in_the_dark_hardcode.highlight_1.title', {
                br: () => <br />,
                tm: (chunks) => <span className='font-sans'>{chunks}</span>,
              })}
            </h2>
            <p className='mt-2 font-criteria text-base text-light md:text-2xl 2xl:text-[28px]'>
              {t('safe_in_the_dark_hardcode.highlight_1.sub_heading')}
            </p>
            <p className='mt-3 text-sm leading-6 text-neutral-200 md:text-base 2xl:text-lg'>
              {t.rich('safe_in_the_dark_hardcode.highlight_1.description', {
                b: (chunks) => <b className='text-white'>{chunks}</b>,
              })}
            </p>
          </div>
          <div className='relative aspect-[985/603] w-full overflow-hidden rounded-2xl'>
            <Image
              src={ImageHelper.getImageUrlCdn(
                '/image/2026/09/18/360reflect-tech.png'
              )}
              alt={t('safe_in_the_dark_hardcode.highlight_1.sub_heading')}
              fill
              className='object-cover'
            />
          </div>
        </CarouselItem>

        {/* Slide 3 — Light follows your movement */}
        <CarouselItem className='relative flex flex-col items-center justify-center gap-6 overflow-hidden bg-neutral-900 px-5 py-10 pb-16 pl-0 md:gap-8 lg:px-15 lg:py-14 lg:pb-20'>
          <div className={cn(styles.glowBlob, styles.glowBlobTopLeft)} />
          <div className={cn(styles.glowBlob, styles.glowBlobBottomRight)} />

          <h2
            className={cn(
              styles.textGlow,
              'relative text-center font-criteria text-2xl font-semibold uppercase italic text-white md:text-6xl 2xl:text-7xl'
            )}
          >
            {t('safe_in_the_dark_hardcode.tech_slider.slide_3.title')}
          </h2>
          <div className='relative grid w-full grid-cols-1 gap-3 sm:grid-cols-3'>
            {movementItems.map((item, index) => (
              <div
                key={index}
                className='relative aspect-[574/496] w-full overflow-hidden rounded-2xl'
              >
                <Image
                  src={ImageHelper.getImageUrlCdn(item.image)}
                  alt={item.description}
                  fill
                  className='object-cover'
                />
                <div className='absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 to-transparent' />
                <p className='absolute bottom-3 left-0 right-0 px-4 text-base text-white 2xl:text-lg'>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </CarouselItem>
      </CarouselContent>

      <CarouselDots className={cn(styles.dots, 'bottom-4')} />
    </Carousel>
  )
}
